import React, { useState, useEffect, useCallback } from 'react';
import { Camera, X, ZoomIn, ZoomOut, Download, ExternalLink, ChevronLeft, ChevronRight, Terminal, FileText } from 'lucide-react';

/**
 * Composant de galerie de screenshots avec zoom
 * Affiche les captures d'écran en miniatures et permet le zoom au clic
 * Supporte les captures console (fichiers .txt)
 */
const ScreenshotGallery = ({ screenshots, scanId }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState({});
  const [consoleContent, setConsoleContent] = useState('');

  // URL de base du backend
  const backendUrl = process.env.REACT_APP_API_URL || '';

  // Gestion du clavier pour navigation et fermeture
  const handleKeyDown = useCallback((e) => {
    if (!selectedImage) return;

    switch (e.key) {
      case 'Escape':
        closeModal();
        break;
      case 'ArrowLeft':
        navigatePrev();
        break;
      case 'ArrowRight':
        navigateNext();
        break;
      case '+':
      case '=':
        setZoom(z => Math.min(z + 0.25, 3));
        break;
      case '-':
        setZoom(z => Math.max(z - 0.25, 0.5));
        break;
      default:
        break;
    }
  }, [selectedImage, selectedIndex, screenshots]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Charger le contenu console
  const loadConsoleContent = async (screenshot) => {
    if (!isConsoleCapture(screenshot)) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(getImageUrl(screenshot.path), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const text = await response.text();
      setConsoleContent(text);
    } catch (error) {
      setConsoleContent('Erreur de chargement du contenu console');
    }
  };

  // Ouvrir le modal avec l'image sélectionnée
  const openModal = (screenshot, index) => {
    setSelectedImage(screenshot);
    setSelectedIndex(index);
    setZoom(1);
    setConsoleContent('');
    if (isConsoleCapture(screenshot)) {
      loadConsoleContent(screenshot);
    }
    document.body.style.overflow = 'hidden';
  };

  // Fermer le modal
  const closeModal = () => {
    setSelectedImage(null);
    setZoom(1);
    document.body.style.overflow = 'auto';
  };

  // Navigation
  const navigatePrev = () => {
    if (screenshots && screenshots.length > 1) {
      const newIndex = selectedIndex === 0 ? screenshots.length - 1 : selectedIndex - 1;
      setSelectedIndex(newIndex);
      setSelectedImage(screenshots[newIndex]);
      setZoom(1);
    }
  };

  const navigateNext = () => {
    if (screenshots && screenshots.length > 1) {
      const newIndex = selectedIndex === screenshots.length - 1 ? 0 : selectedIndex + 1;
      setSelectedIndex(newIndex);
      setSelectedImage(screenshots[newIndex]);
      setZoom(1);
    }
  };

  // Vérifier si c'est une capture console (fichier .txt)
  const isConsoleCapture = (screenshot) => {
    return screenshot?.type === 'console' ||
           screenshot?.path?.endsWith('.txt') ||
           screenshot?.url?.startsWith('console://');
  };

  // Construire l'URL complète de l'image
  const getImageUrl = (path) => {
    if (!path) return '';
    // Si le path commence par http, c'est déjà une URL complète
    if (path.startsWith('http')) return path;
    // Sinon, construire l'URL avec le backend
    return `${backendUrl}${path}`;
  };

  // Télécharger l'image
  const downloadImage = (screenshot) => {
    const link = document.createElement('a');
    link.href = getImageUrl(screenshot.path);
    link.download = screenshot.path.split('/').pop() || 'screenshot.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Gérer le chargement des images
  const handleImageLoad = (index) => {
    setLoading(prev => ({ ...prev, [index]: false }));
  };

  const handleImageError = (index) => {
    setLoading(prev => ({ ...prev, [index]: 'error' }));
  };

  if (!screenshots || screenshots.length === 0) {
    return null;
  }

  return (
    <>
      {/* Galerie de miniatures */}
      <div className="mt-4 p-4 bg-brand-black/50 rounded-lg border border-brand-green/20">
        <div className="flex items-center gap-2 mb-3">
          <Camera className="w-4 h-4 text-brand-green" />
          <span className="text-sm font-medium text-brand-green">
            Captures d'écran ({screenshots.length})
          </span>
        </div>

        <div className="flex flex-wrap gap-3">
          {screenshots.map((screenshot, index) => (
            <div
              key={index}
              className="relative group cursor-pointer"
              onClick={() => openModal(screenshot, index)}
            >
              {/* Miniature */}
              <div className="w-32 h-24 bg-gray-800 rounded-lg overflow-hidden border-2 border-transparent hover:border-brand-green transition-all duration-200 shadow-lg hover:shadow-brand-green/20">
                {isConsoleCapture(screenshot) ? (
                  /* Capture console - afficher une icône terminal */
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900">
                    <Terminal className="w-8 h-8 text-brand-green mb-1" />
                    <span className="text-xs text-gray-400">Console</span>
                  </div>
                ) : loading[index] !== 'error' ? (
                  <img
                    src={getImageUrl(screenshot.path)}
                    alt={screenshot.description || `Screenshot ${index + 1}`}
                    className="w-full h-full object-cover"
                    onLoad={() => handleImageLoad(index)}
                    onError={() => handleImageError(index)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-red-500/10">
                    <Camera className="w-6 h-6 text-red-400" />
                  </div>
                )}

                {/* Overlay au hover */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn className="w-6 h-6 text-white" />
                </div>
              </div>

              {/* Label sous la miniature */}
              <div className="mt-1 text-xs text-gray-400 text-center truncate max-w-[128px]">
                {isConsoleCapture(screenshot) ? 'Console' :
                  screenshot.url && screenshot.url.startsWith('http') ?
                    new URL(screenshot.url).hostname : 'Capture'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de zoom */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={closeModal}
        >
          {/* Toolbar */}
          <div className="absolute top-4 right-4 flex items-center gap-2 z-50">
            <button
              onClick={(e) => { e.stopPropagation(); setZoom(z => Math.max(z - 0.25, 0.5)); }}
              className="p-2 bg-brand-black/80 hover:bg-brand-green/20 rounded-lg border border-gray-700 transition-colors"
              title="Dézoomer (-)"
            >
              <ZoomOut className="w-5 h-5 text-white" />
            </button>
            <span className="px-3 py-2 bg-brand-black/80 rounded-lg border border-gray-700 text-white text-sm min-w-[60px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); setZoom(z => Math.min(z + 0.25, 3)); }}
              className="p-2 bg-brand-black/80 hover:bg-brand-green/20 rounded-lg border border-gray-700 transition-colors"
              title="Zoomer (+)"
            >
              <ZoomIn className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); downloadImage(selectedImage); }}
              className="p-2 bg-brand-black/80 hover:bg-brand-green/20 rounded-lg border border-gray-700 transition-colors"
              title="Télécharger"
            >
              <Download className="w-5 h-5 text-white" />
            </button>
            <a
              href={getImageUrl(selectedImage.path)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2 bg-brand-black/80 hover:bg-brand-green/20 rounded-lg border border-gray-700 transition-colors"
              title="Ouvrir dans un nouvel onglet"
            >
              <ExternalLink className="w-5 h-5 text-white" />
            </a>
            <button
              onClick={closeModal}
              className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded-lg border border-red-500/50 transition-colors"
              title="Fermer (Esc)"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Navigation gauche */}
          {screenshots.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigatePrev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-brand-black/80 hover:bg-brand-green/20 rounded-full border border-gray-700 transition-colors z-50"
              title="Précédent (←)"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}

          {/* Navigation droite */}
          {screenshots.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateNext(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-brand-black/80 hover:bg-brand-green/20 rounded-full border border-gray-700 transition-colors z-50"
              title="Suivant (→)"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}

          {/* Contenu principal - Image ou Console */}
          <div
            className="max-w-[90vw] max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {isConsoleCapture(selectedImage) ? (
              /* Affichage console */
              <div className="bg-gray-900 rounded-lg shadow-2xl p-4 min-w-[600px] max-w-[80vw]">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700">
                  <Terminal className="w-5 h-5 text-brand-green" />
                  <span className="text-brand-green font-medium">Console Output</span>
                </div>
                <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono overflow-auto max-h-[70vh]">
                  {consoleContent || 'Chargement...'}
                </pre>
              </div>
            ) : (
              <img
                src={getImageUrl(selectedImage.path)}
                alt={selectedImage.description || 'Screenshot'}
                className="transition-transform duration-200 rounded-lg shadow-2xl"
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'center center',
                  maxWidth: zoom === 1 ? '100%' : 'none',
                  maxHeight: zoom === 1 ? '85vh' : 'none'
                }}
              />
            )}
          </div>

          {/* Info bar en bas */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-brand-black/90 rounded-lg border border-gray-700 z-50">
            <div className="flex items-center gap-4">
              <span className="text-white font-medium">
                {selectedImage.description || 'Screenshot'}
              </span>
              {selectedImage.url && (
                <span className="text-gray-400 text-sm">
                  {selectedImage.url}
                </span>
              )}
              {screenshots.length > 1 && (
                <span className="text-brand-green text-sm">
                  {selectedIndex + 1} / {screenshots.length}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScreenshotGallery;
